import React from 'react';
import {
  getDeclarationFields,
  isOnlineDeclaration,
  isPrintDeclarationMode,
  isPurePrintDeclaration,
  resolveTypeConfig,
} from '../../../utils/applicationQuestionTypeConfig';
import { getRenderFamily, isNonAnswerContentType } from '../../../utils/applicationQuestionTypes';

function fieldInputTypeToHtml(inputType) {
  if (inputType === 'email') return 'email';
  if (inputType === 'date') return 'date';
  if (inputType === 'textarea' || inputType === 'signature') return 'textarea';
  return 'text';
}

/** Renders declaration / consent blocks using configurable typeConfig.fields */
export function DeclarationBlock({
  question,
  value,
  onChange,
  readOnly = false,
  hasError = false,
}) {
  const content = question?.label || '';
  const cfg = resolveTypeConfig(question);
  const fields = getDeclarationFields(question);
  const printMode = isPrintDeclarationMode(question);
  const onlineMode = isOnlineDeclaration(question);
  const val = value && typeof value === 'object' ? value : {};

  const setField = (key, fieldValue) => {
    if (readOnly || !onChange) return;
    onChange({ ...val, [key]: fieldValue });
  };

  return (
    <div className="rounded border border-gray-200 bg-gray-50 p-4 text-sm text-gray-800 leading-relaxed space-y-4">
      <div className="whitespace-pre-wrap">{content}</div>
      <div className={`space-y-3 pt-2 border-t border-gray-200 ${hasError ? 'border border-red-400 rounded bg-red-50 p-2' : ''}`}>
        {cfg.printNote ? (
          <p className="text-[11px] text-gray-500 italic">{cfg.printNote}</p>
        ) : null}
        {fields.map((field) => {
          const fKey = field.key;
          const inputType = field.inputType || 'text';
          const ro = readOnly || Boolean(field.readOnly);

          if (inputType === 'checkbox') {
            return (
              <label key={fKey} className={`flex items-start gap-3 ${ro ? 'cursor-default' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={!!val[fKey]}
                  onChange={(e) => setField(fKey, e.target.checked)}
                  disabled={ro}
                  className="mt-0.5 h-4 w-4 rounded border-gray-300 accent-indigo-600"
                />
                <span className="text-sm text-gray-900">
                  {field.label}
                  {!ro && field.required && <span className="text-red-500 ml-1">*</span>}
                </span>
              </label>
            );
          }

          const htmlType = fieldInputTypeToHtml(inputType);
          const isTextarea = htmlType === 'textarea';

          return (
            <div key={fKey}>
              <label className="block text-sm font-medium text-gray-900 mb-1">
                {field.label}
                {!ro && field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              {isTextarea ? (
                <textarea
                  value={String(val[fKey] ?? '')}
                  onChange={(e) => setField(fKey, e.target.value)}
                  readOnly={ro}
                  disabled={ro}
                  rows={field.rows || (inputType === 'signature' ? 6 : 3)}
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                />
              ) : (
                <input
                  type={htmlType}
                  value={String(val[fKey] ?? '')}
                  onChange={(e) => setField(fKey, e.target.value)}
                  readOnly={ro}
                  disabled={ro}
                  className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100"
                />
              )}
            </div>
          );
        })}
        {printMode && !onlineMode && fields.length === 0 && (
          <p className="text-xs text-gray-500 italic">No fields configured — add fields in the builder.</p>
        )}
      </div>
    </div>
  );
}

export function SignaturePadPreview({ question, readOnly = true }) {
  const cfg = resolveTypeConfig(question);
  return (
    <div
      className="rounded border-2 border-dashed border-gray-300 bg-white flex items-center justify-center text-xs text-gray-400"
      style={{ minHeight: cfg.height || 160, backgroundColor: cfg.backgroundColor || '#fff' }}
    >
      {readOnly ? (cfg.instruction || 'Signature area') : 'Sign here'}
    </div>
  );
}

export function isDeclarationQuestion(question) {
  const f = getRenderFamily(question?.type);
  return f === 'declaration-print' || f === 'declaration-consent' || f === 'declaration-custom';
}

export function isStaticContentQuestion(question) {
  const f = getRenderFamily(question?.type);
  return f === 'static' || f === 'section-header' || f === 'callout' || f === 'html' || f === 'divider' || f === 'piped-display';
}

/** Hide content-only / print-declaration questions from answer review lists */
export function shouldHideQuestionInAnswerList(questionOrType) {
  const type = typeof questionOrType === 'string' ? questionOrType : questionOrType?.type;
  if (isNonAnswerContentType(type)) return true;
  const q = typeof questionOrType === 'object' ? questionOrType : { type };
  if (isPurePrintDeclaration(q)) return true;
  return false;
}

/** Summary line for online declaration / consent answers in review screens */
export function renderDeclarationAnswerSummary(question, value) {
  if (!value) return null;
  const fields = getDeclarationFields(question);
  const val = typeof value === 'object' ? value : {};
  const checkboxes = fields.filter((f) => f.inputType === 'checkbox');
  const agreed = checkboxes.length
    ? checkboxes.every((f) => val[f.key])
    : Object.keys(val).some((k) => val[k]);
  const dateField = fields.find((f) => f.inputType === 'date');
  const date = dateField ? val[dateField.key] : null;
  return agreed
    ? { agreed: true, date }
    : { agreed: false, date };
}

/** Validate declaration/consent value against configured fields */
export function validateDeclarationValue(question, value) {
  if (isPurePrintDeclaration(question)) return null;
  const fields = getDeclarationFields(question).filter((f) => f.required && !f.readOnly);
  const val = value && typeof value === 'object' ? value : {};
  for (const field of fields) {
    if (field.inputType === 'checkbox') {
      if (!val[field.key]) return `${field.label} is required`;
    } else if (!String(val[field.key] ?? '').trim()) {
      return `${field.label} is required`;
    }
  }
  return null;
}
